import React, { useCallback, useContext, useState } from 'react'
import { Trans, t } from '@lingui/macro'
import { v4 as uuidv4 } from 'uuid'
import { css } from '@emotion/react'
import { debounce } from 'lodash'

import { LooopS3BucketEnum } from 'front-end/src/graphql/private/PrivateSchemaTypes'
import { ButtonClose, ButtonIcon, ButtonLight } from 'front-end/src/components'

import { SurveyQuestionTypes } from '../../../../../../shared/surveys'
import Form, { FormRow } from '../../../../components/common/form/Form'
import Label from '../../../../components/common/form/label/Label'
import Textarea from '../../../../components/common/form/textarea/Textarea'
import { notification } from '../../../../components/common/notifications/Notifications'
import EditorQuill from '../../../../components/common/quill/EditorQuill'
import {
  isSurveyEditable,
  getSurveyTypeImage,
  getSurveyTypeName,
  filterInfoQuestions
} from '../../helpers'
import { ISurveyQuestion } from '../../types'

import SurveyQuestionDeleteModal from './SurveyQuestionDeleteModal'
import { SurveyQuestionDuplicateModal } from './SurveyQuestionDuplicateModal'
import { SurveyQuestionOptions } from './SurveyQuestionOptions'
import { LooopS3FileUpload } from '../../../../app/util/LooopS3FileUpload'
import { replaceBase64ImagesInHtml } from '../../../topic-detail-page/components/utils'
import { useUpdateSurveyQuestionMutation } from './operations/updateSurveyQuestion.codegen'
import './SurveyQuestion.less'
import { brandColors } from 'shared/basicPalette'
import { ManageSurveyContext } from '../ManageSurveyContext'
import IconDuplicate from 'front-end/src/components/common/icon/IconDuplicate'
import IconPlus2 from 'front-end/src/components/common/icon/IconPlus2'

interface ISurveyQuestionProps {
  question: ISurveyQuestion
  index: number
  DragHandle: React.ComponentClass
}

const SurveyQuestion = ({ question, DragHandle }: ISurveyQuestionProps) => {
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false)
  const [showDuplicateModal, setShowDuplicateModal] = useState<boolean>(false)
  const { survey, questions, refetchSurvey } = useContext(ManageSurveyContext)
  const [localQuestion, setLocalQuestion] = useState(question)
  const [uploadPercentageCompleted, setUploadPercentageCompleted] = useState(0)
  const questionsWithoutInfo = filterInfoQuestions(questions)

  const [updateQuestionMutation] = useUpdateSurveyQuestionMutation()

  const renderAddOptionButton = () => {
    if (!survey.published_at && !survey.completed_at) {
      return (
        <div className="SurveyQuestion__addOption">
          <ButtonLight
            disabled={!isSurveyEditable(survey)}
            onClick={async () => {
              if (
                localQuestion.content?.options?.length &&
                localQuestion.content?.options?.length > 9
              ) {
                const maxValue = 10
                notification.warning(t`Maximum is ${maxValue} options`)
                return null
              }

              updateOptions([
                ...(localQuestion?.content?.options || []),
                { id: uuidv4(), label: '' }
              ])
            }}
            leftIcon={<IconPlus2 />}
          >
            <Trans>Add an option</Trans>
          </ButtonLight>
        </div>
      )
    }
  }

  const updateQuestion = useCallback(
    debounce(async (input) => {
      const { errors } = await updateQuestionMutation({
        variables: {
          id: localQuestion.id,
          input,
          surveyId: survey.id
        }
      })

      if (errors) {
        notification.error(t`Question couldn't be updated, please try again.`)
        refetchSurvey()
      }
    }, 500),
    []
  )

  const patchQuestion = (patch) => {
    setLocalQuestion((current) => {
      // always send content
      updateQuestion({ content: current.content, ...patch })

      // always use current state
      return { ...current, ...patch }
    })
  }

  const updateOptions = (options) =>
    patchQuestion({ content: { ...localQuestion.content, options } })
  const iconButtonStyle = {
    color: brandColors.grey.mid,
    minW: '32px',
    h: '32px',
    bg: 'none',
    _hover: {
      bg: brandColors.grey.lighter
    }
  }
  return (
    <Form className="SurveyQuestion" key={localQuestion.id}>
      {isSurveyEditable(survey) && (
        <aside className="SurveyQuestion__actions">
          <ButtonClose
            {...iconButtonStyle}
            onClick={() => setShowDeleteModal(true)}
          />
          <ButtonIcon
            {...iconButtonStyle}
            aria-label={t`Drag`}
            css={css`
              .icon-menu {
                width: 100%;
                line-height: 32px;
              }
            `}
          >
            <DragHandle />
          </ButtonIcon>
          <ButtonIcon {...iconButtonStyle} aria-label={t`Duplicate`}>
            <IconDuplicate onClick={() => setShowDuplicateModal(true)} />
          </ButtonIcon>
        </aside>
      )}
      <div className="SurveyQuestion__header">
        <h3>
          {localQuestion.type === SurveyQuestionTypes.INFO
            ? 'Information'
            : `Question ${questionsWithoutInfo.indexOf(question) + 1}`}
        </h3>
        <div className="SurveyQuestion__type">
          <img
            src={getSurveyTypeImage(localQuestion.type)}
            alt={localQuestion.type ?? ''}
          />
          {getSurveyTypeName(localQuestion.type)}
        </div>
      </div>

      {localQuestion.type === SurveyQuestionTypes.INFO ? (
        <FormRow className="SurveyQuestion__editor">
          <Label />
          <EditorQuill
            readOnly={!isSurveyEditable(survey)}
            value={localQuestion.description}
            onChange={async (content) => {
              const html = await replaceBase64ImagesInHtml(
                content,
                {
                  S3path: `survey-images/survey-${survey.id}`,
                  bucket: LooopS3BucketEnum.PRIVATE
                },
                () => {}
              )

              patchQuestion({ description: html })
            }}
            onUploadProgress={setUploadPercentageCompleted}
            uploadFn={async (fileName, blob) => {
              return LooopS3FileUpload({
                S3UploadProps: {
                  filename: fileName,
                  S3path: `survey-images/survey-${survey.id}`,
                  bucket: LooopS3BucketEnum.PRIVATE
                },
                blob: blob,
                on: {
                  uploadProgress: setUploadPercentageCompleted
                }
              })
            }}
          />
          {uploadPercentageCompleted !== 100 && (
            <div
              style={{ width: `${uploadPercentageCompleted}%` }}
              className="SurveyQuestion__editor__loader"
            />
          )}
        </FormRow>
      ) : (
        <FormRow className="SurveyQuestion__title">
          <Label>
            <Trans>Question</Trans>
          </Label>
          <Textarea
            disabled={!isSurveyEditable(survey)}
            value={localQuestion.title ?? ''}
            placeholder={t`Question title comes here`}
            title={t`Question title`}
            rows={1}
            onChange={(title) => patchQuestion({ title })}
          />
        </FormRow>
      )}

      {(localQuestion.type === SurveyQuestionTypes.CHOOSE_ONE ||
        localQuestion.type === SurveyQuestionTypes.MULTIPLE_CHOICE) && (
        <>
          <SurveyQuestionOptions
            question={localQuestion}
            allowDelete={true}
            updateOptions={updateOptions}
          />
          {renderAddOptionButton()}
        </>
      )}

      {localQuestion.type === SurveyQuestionTypes.BOOL && (
        <SurveyQuestionOptions
          question={localQuestion}
          allowDelete={false}
          updateOptions={updateOptions}
        />
      )}

      {localQuestion.type === SurveyQuestionTypes.RATING && (
        <>
          <SurveyQuestionOptions
            question={localQuestion}
            allowDelete={true}
            updateOptions={updateOptions}
          />
          {renderAddOptionButton()}
        </>
      )}
      <SurveyQuestionDeleteModal
        show={showDeleteModal}
        onHide={() => {
          setShowDeleteModal(false)
        }}
        questionId={localQuestion.id}
      />
      <SurveyQuestionDuplicateModal
        show={showDuplicateModal}
        onHide={() => {
          setShowDuplicateModal(false)
        }}
        questionId={localQuestion.id}
      />
    </Form>
  )
}
export default SurveyQuestion
